import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Loader2, MapPin, Calendar } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import dict from "../../i18n/es-MX";

const STATUS_COLOR = {
    programado: "border-sky-500/40 text-sky-300",
    en_progreso: "border-brand-orange text-brand-orange",
    completado: "border-emerald-500/40 text-emerald-300",
    cancelado: "border-slate-700 text-slate-500",
};
const TAB_STATUS = {
    active: "en_progreso",
    upcoming: "programado",
    past: "past",
};
import { fmtDate } from "../../lib/dateFormat";

export default function OperatorJobs() {
    const [tab, setTab] = useState("active");
    const [counts, setCounts] = useState({ active: null, upcoming: null, past: null });
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showAll, setShowAll] = useState(false);

    // Load counts for all tabs in parallel
    useEffect(() => {
        (async () => {
            try {
                const [a, u, p] = await Promise.all([
                    api.get("/jobs/my", { params: { status: "en_progreso" } }),
                    api.get("/jobs/my", { params: { status: "programado" } }),
                    api.get("/jobs/my", { params: { status: "past" } }),
                ]);
                setCounts({ active: a.data.length, upcoming: u.data.length, past: p.data.length });
            } catch { /* ignore */ }
        })();
    }, []);

    const loadTab = useCallback(async (t) => {
        setLoading(true); setError("");
        try {
            const r = await api.get("/jobs/my", { params: { status: TAB_STATUS[t] } });
            setJobs(r.data);
            setShowAll(false);
        } catch (e) { setError(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { loadTab(tab); }, [tab, loadTab]);

    const visibleJobs = tab === "past" && !showAll ? jobs.slice(0, 20) : jobs;

    const tabStyle = (t) => tab === t
        ? "bg-brand-orange text-slate-950 font-semibold border-brand-orange"
        : "bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500";

    return (
        <div data-testid="operator-jobs" className="px-4 py-5 space-y-4">
            <header>
                <h1 className="font-display text-3xl font-extrabold tracking-tight">
                    {dict.jobs.operator.myJobs}
                </h1>
                <p className="text-xs text-slate-500 mt-1">{dict.jobs.operator.tapForDetail}</p>
            </header>

            {/* Sub-tabs */}
            <div className="grid grid-cols-3 gap-2" data-testid="operator-jobs-tabs">
                {["active", "upcoming", "past"].map((t) => (
                    <button key={t} type="button" onClick={() => setTab(t)}
                        data-testid={`operator-jobs-tab-${t}`}
                        className={`h-11 px-2 rounded-lg border text-xs uppercase tracking-wider transition inline-flex items-center justify-center gap-1 ${tabStyle(t)}`}>
                        <span>{dict.myJobsTabs[t]}</span>
                        {counts[t] != null && (
                            <span className={`text-[10px] font-mono ${tab===t?"text-slate-950/70":"text-slate-500"}`}>({counts[t]})</span>
                        )}
                    </button>
                ))}
            </div>

            {loading && (
                <div className="py-10 text-center"><Loader2 className="animate-spin inline text-slate-500"/></div>
            )}
            {!loading && error && (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300" data-testid="operator-jobs-error">{error}</div>
            )}
            {!loading && !error && jobs.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 p-10 text-center" data-testid={`operator-jobs-empty-${tab}`}>
                    <Briefcase className="mx-auto text-brand-orange mb-3" size={36} strokeWidth={1.8}/>
                    <p className="text-sm text-slate-400">{dict.myJobsTabs.empty[tab]}</p>
                </div>
            )}

            {!loading && visibleJobs.map((j) => (
                <Link
                    key={j.id}
                    to={`/campo/trabajos/${j.id}`}
                    data-testid={`my-job-card-${j.id}`}
                    className={`block rounded-xl border bg-slate-900/60 px-4 py-3 transition hover:bg-slate-900 ${STATUS_COLOR[j.status]||"border-slate-700"} ${tab==="past"?"opacity-70":""}`}
                >
                    <div className="flex items-start justify-between gap-2">
                        <div className="font-mono text-xs text-slate-400">{j.code}</div>
                        <span className="text-[10px] uppercase tracking-wider font-semibold">
                            {dict.jobs.statusLabel[j.status]}
                        </span>
                    </div>
                    <div className="mt-1 text-base font-semibold text-slate-100">{j.client_name || "—"}</div>
                    <p className="mt-1 text-sm text-slate-300 line-clamp-2">{j.description}</p>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-400">
                        <span className="inline-flex items-center gap-1"><Calendar size={12}/>{fmtDate(j.start_date)}{j.end_date && ` → ${fmtDate(j.end_date)}`}</span>
                        <span className="inline-flex items-center gap-1"><MapPin size={12}/>{j.location_text}</span>
                    </div>
                </Link>
            ))}

            {tab === "past" && !showAll && jobs.length > 20 && (
                <button type="button" onClick={() => setShowAll(true)} data-testid="operator-jobs-load-more"
                    className="w-full h-11 rounded-lg border border-slate-700 bg-slate-900 hover:bg-slate-800 text-sm text-slate-300">
                    {dict.myJobsTabs.loadMore} ({jobs.length - 20})
                </button>
            )}
        </div>
    );
}
